import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, X, Basketball, Calendar, Warning, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import TeamLogo from "./TeamLogo";

const WNBA_ACCENT = "#FF6B35";
const CHARLIE = "#22C55E";
const DAKOTA = "#F59E0B";

const tomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

function TeamRow({ team, onRemove, disabled }) {
  return (
    <div className="flex items-center justify-between bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2" data-testid={`wnba-team-row-${team.abbr}`}>
      <div className="flex items-center gap-2.5">
        <TeamLogo sport="wnba" abbr={team.abbr} size={28} />
        <div>
          <div className="text-sm font-bold">{team.name}</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">{team.abbr}</div>
        </div>
      </div>
      <button
        onClick={onRemove}
        disabled={disabled}
        data-testid={`wnba-remove-${team.abbr}`}
        className="text-zinc-500 hover:text-[#FF3B30] disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <X size={16} />
      </button>
    </div>
  );
}

function StrategyPanel({ label, accent, activeAbbrs, allTeams, onAdd, onRemove, busy }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedAbbr, setSelectedAbbr] = useState("");
  const activeTeams = allTeams.filter((t) => activeAbbrs.includes(t.abbr));
  const remaining = allTeams.filter((t) => !activeAbbrs.includes(t.abbr));
  const isEmpty = activeTeams.length === 0;

  return (
    <div className="bg-[#121212] border rounded-sm p-5" style={{ borderColor: `${accent}66` }} data-testid={`wnba-panel-${label.toLowerCase()}`}>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Analyst · WNBA</div>
          <h3 className="font-display text-3xl">{label}</h3>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Active teams</div>
          <div className="font-display text-3xl" style={{ color: accent }}>{activeTeams.length}</div>
        </div>
      </div>

      {isEmpty && (
        <div className="border border-dashed border-white/15 rounded-sm px-3 py-3 text-xs text-zinc-400 mb-3" data-testid={`wnba-panel-${label.toLowerCase()}-paused`}>
          <span className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold">Strategy paused</span>
          <div className="text-zinc-500 mt-0.5">No teams selected — {label} will not generate any WNBA picks until you add at least one team.</div>
        </div>
      )}

      <div className="space-y-2 mb-3">
        {activeTeams.map((t) => (
          <TeamRow key={t.abbr} team={t} onRemove={() => onRemove(t.abbr)} disabled={busy} />
        ))}
      </div>

      {pickerOpen ? (
        <div className="space-y-2">
          <select
            value={selectedAbbr}
            onChange={(e) => setSelectedAbbr(e.target.value)}
            data-testid={`wnba-add-select-${label.toLowerCase()}`}
            className="w-full bg-[#0A0A0A] border border-white/15 rounded-sm px-3 py-2 text-sm font-mono-d"
          >
            <option value="">Choose a team…</option>
            {remaining.map((t) => (
              <option key={t.abbr} value={t.abbr}>{t.name} ({t.abbr})</option>
            ))}
          </select>
          <div className="flex gap-2">
            <button
              onClick={() => { setPickerOpen(false); setSelectedAbbr(""); }}
              className="flex-1 py-2 border border-white/15 hover:border-white/40 text-xs uppercase tracking-wider rounded-sm"
            >
              Cancel
            </button>
            <button
              onClick={async () => { if (!selectedAbbr) return; await onAdd(selectedAbbr); setPickerOpen(false); setSelectedAbbr(""); }}
              disabled={!selectedAbbr || busy}
              data-testid={`wnba-confirm-add-${label.toLowerCase()}`}
              className="flex-1 py-2 disabled:opacity-40 text-black text-xs uppercase tracking-wider font-bold rounded-sm"
              style={{ background: accent }}
            >
              Add team
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setPickerOpen(true)}
          disabled={busy || remaining.length === 0}
          data-testid={`wnba-open-add-${label.toLowerCase()}`}
          className="w-full flex items-center justify-center gap-2 py-2 border border-dashed border-white/15 hover:border-white/40 text-xs uppercase tracking-wider text-zinc-400 rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus size={14} /> {remaining.length === 0 ? "All teams added" : "Add team"}
        </button>
      )}
    </div>
  );
}

function HistoryRow({ change, onRevert }) {
  const inFuture = change.effective_date > today();
  const verb = change.action === "add" ? "Add" : "Remove";
  const verbColor = change.action === "add" ? "text-[#22C55E]" : "text-[#FF3B30]";
  return (
    <div className="flex items-center justify-between border-b border-white/5 py-2 text-xs" data-testid={`wnba-history-${change.change_id}`}>
      <div className="flex items-baseline gap-3">
        <span className={`text-[10px] uppercase tracking-widest font-bold ${verbColor}`}>{verb}</span>
        <span className="font-bold">{change.team_abbr}</span>
        <span className="text-zinc-500">→ {change.strategy === "charlie" ? "Charlie" : "Dakota"}</span>
        <span className="text-zinc-600 font-mono-d">eff. {change.effective_date}</span>
      </div>
      {inFuture && (
        <button onClick={onRevert} data-testid={`wnba-revert-${change.change_id}`} className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-[#FF3B30]">
          Revert
        </button>
      )}
    </div>
  );
}

export default function StrategyWnbaTeams() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pendingDate, setPendingDate] = useState(today());

  const load = async () => {
    try {
      const { data } = await api.get("/admin/strategy/wnba-teams");
      setState(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load WNBA strategy state");
    }
  };
  useEffect(() => { load(); }, []);

  const doChange = async (strategy, action, abbr) => {
    setBusy(true);
    try {
      const { data: resp } = await api.post("/admin/strategy/wnba-teams", {
        strategy, action, team_abbr: abbr, effective_date: pendingDate,
      });
      const analyst = strategy === "charlie" ? "Charlie" : "Dakota";
      const effLabel = pendingDate === today() ? "TODAY" : `eff. ${pendingDate}`;
      const newly = resp?.relocked?.newly_locked || 0;
      const lockedTail = action === "add" && resp?.relocked?.triggered
        ? (newly > 0 ? ` · ${newly} new pick${newly === 1 ? "" : "s"} locked for today` : " · today's slate unchanged (no new triggers)")
        : "";
      toast.success(`${action === "add" ? "Added" : "Removed"} ${abbr} → ${analyst} (${effLabel})${lockedTail}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Change failed");
    } finally {
      setBusy(false);
    }
  };

  const revert = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/admin/strategy/wnba-teams/${id}`);
      toast.success("Queued change reverted");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revert failed");
    } finally { setBusy(false); }
  };

  const lockNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/wnba/picks/lock-now", {});
      if (data.newly_locked === 0 && data.already_locked === 0) {
        toast.info(`No WNBA picks qualified for today (${data.target_date})`);
      } else {
        toast.success(`Locked ${data.newly_locked} new WNBA picks (${data.already_locked} already locked)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lock failed");
    } finally { setBusy(false); }
  };

  if (!state) return <div className="text-zinc-500 font-mono-d py-6 text-sm" data-testid="wnba-strategy-loading">Loading WNBA strategy state…</div>;

  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className="mt-12 pt-10 border-t border-white/10"
      data-testid="wnba-strategy-section"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-sm flex items-center justify-center" style={{ background: `${WNBA_ACCENT}1F`, border: `1px solid ${WNBA_ACCENT}66` }}>
          <Basketball size={20} weight="fill" style={{ color: WNBA_ACCENT }} />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em]" style={{ color: WNBA_ACCENT }}>WNBA · Strategy Control</div>
          <h2 className="font-display text-4xl sm:text-5xl leading-none mt-1">WNBA TEAMS</h2>
        </div>
      </div>
      <p className="text-sm text-zinc-400 mt-3 mb-6 max-w-3xl">
        Select which WNBA teams Charlie's iso-1L and Dakota's W1-after-L strategies apply to. Picks are generated daily at 09:00 PT and locked before tipoff.
        <span className="text-yellow-400"> Leaving a strategy empty pauses it</span> — no picks will fire for that analyst.
      </p>

      <div className="bg-[#121212] border border-white/10 rounded-sm p-4 mb-6 flex items-center gap-3 flex-wrap" data-testid="wnba-effective-date-bar">
        <Calendar size={16} style={{ color: WNBA_ACCENT }} />
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">Effective date for next change</div>
        <input
          type="date"
          value={pendingDate}
          min={today()}
          onChange={(e) => setPendingDate(e.target.value)}
          data-testid="wnba-effective-date-input"
          className="bg-[#0A0A0A] border border-white/15 rounded-sm px-3 py-1.5 text-sm font-mono-d"
        />
        <div className="text-[10px] text-zinc-500 italic flex items-center gap-1">
          <Warning size={12} /> changes effective today re-lock today's slate automatically
        </div>
        <button
          onClick={lockNow}
          disabled={busy}
          data-testid="wnba-lock-now"
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/15 hover:border-white/40 text-xs uppercase tracking-widest rounded-sm transition-colors disabled:opacity-40"
        >
          <ArrowsClockwise size={12} className={busy ? "animate-spin" : ""} /> Lock today's picks now
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
        <StrategyPanel
          label="Charlie"
          accent={CHARLIE}
          activeAbbrs={state.active.charlie}
          allTeams={state.all_teams}
          onAdd={(ab) => doChange("charlie", "add", ab)}
          onRemove={(ab) => doChange("charlie", "remove", ab)}
          busy={busy}
        />
        <StrategyPanel
          label="Dakota"
          accent={DAKOTA}
          activeAbbrs={state.active.dakota}
          allTeams={state.all_teams}
          onAdd={(ab) => doChange("dakota", "add", ab)}
          onRemove={(ab) => doChange("dakota", "remove", ab)}
          busy={busy}
        />
      </div>

      <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
        <h3 className="font-display text-2xl tracking-wider mb-3">Change History</h3>
        {state.history.length === 0 ? (
          <div className="text-xs text-zinc-500 italic">No changes yet — both strategies start empty. Add teams above to begin generating picks.</div>
        ) : (
          <div data-testid="wnba-history-list">{state.history.map((c) => (<HistoryRow key={c.change_id} change={c} onRevert={() => revert(c.change_id)} />))}</div>
        )}
      </div>
    </motion.section>
  );
}
